import { registerLocaleData } from '@angular/common';
import localePT from '@angular/common/locales/pt';
import { LOCALE_ID, NgModule } from '@angular/core';
import { FirebaseApp, getApp, initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getAuth, provideAuth } from '@angular/fire/auth';
import { getFirestore, provideFirestore } from '@angular/fire/firestore';
import { getMessaging, isSupported } from '@angular/fire/messaging';
import { FormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { ButtonComponent, IconComponent, ModalComponent, providePatron } from '@teorema/patron';
import { MarkdownModule } from 'ngx-markdown';
import { UtilsService } from 'src/app/shared';
import { environment } from 'src/environments/environment';
import packageJson from '../../package.json';
//import { navItems } from './_nav';
import { navItemsV2 as navItems } from './_nav-v2';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app.routing';
import { AutoLoginComponent } from './commons/auto-login/auto-login.component';
import { DiagnosticSummaryComponent } from './commons/diagnostic-summary/diagnostic-summary.component';
import { LgpdConsentRequestComponent } from './commons/lgpd-consent-request.component';
import { LicenseSessionLockComponent } from './commons/license-session-lock.component';
import { CoreConfig } from './models/core-config.model';
import { TicketCenterModule } from './modules/ticket-center/ticket-center.module';
import { LayoutV2Module } from './shared/components/layout-v2/layout-v2.module';

registerLocaleData(localePT, 'pt-BR');
@NgModule({
  declarations: [
    AppComponent,
    AutoLoginComponent,
    LgpdConsentRequestComponent,
    LicenseSessionLockComponent,
    DiagnosticSummaryComponent,
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    LayoutV2Module,
    ModalComponent,
    FormsModule,
    IconComponent,
    ButtonComponent,
    MarkdownModule.forRoot(),
    TicketCenterModule,
  ],
  providers: [
    // Patron Configuration (inclui tema do PrimeNG)
    providePatron(),

    // Main Firebase app (for auth and firestore)
    provideFirebaseApp(() => initializeApp(environment.firebaseConfig)),
    provideAuth(() => getAuth()),
    provideFirestore(() => getFirestore()),

    // FCM Firebase app (separate project)
    {
      provide: 'FIREBASE_MESSAGING_APP',
      useFactory: () => {
        try {
          // Initialize FCM app with separate config
          return initializeApp(environment.fcmConfig, 'fcm-app');
        } catch (e) {
          console.error('Error initializing FCM app', e);
          return null;
        }
      },
    },
    {
      provide: 'FIREBASE_MESSAGING',
      useFactory: (fcmApp: FirebaseApp) => {
        try {
          return isSupported().then(supported => {
            if (supported) {
              return getMessaging(fcmApp);
            }
            return null;
          });
        } catch (e) {
          console.error('Error getting messaging instance', e);
          return Promise.resolve(null);
        }
      },
      deps: ['FIREBASE_MESSAGING_APP'],
    },
    // {
    //   provide: ErrorHandler,
    //   useValue: Sentry.createErrorHandler(),
    // },
    {
      provide: LOCALE_ID,
      useValue: 'pt-BR',
    },
    {
      provide: 'config',
      useValue: {
        appVersion: packageJson.version,
        apiUrl: environment.apiUrl,
        apiPhp: environment.apiPhp,
        apiCnpj: environment.apiCnpj,
        system: environment.system,
        panelTitle: environment.title,
        navItems: navItems,
        redirectPathAfterLogin: environment.redirectPathAfterLogin,
        firebaseProjectId: environment.firebaseConfig.projectId,
        env: environment.env,
        fcmConfig: environment.fcmConfig,
        enableSwitchTheme: true,
        repositorySlug: 'multipanel',
      } as CoreConfig,
    },
  ],

  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(utilsService: UtilsService) {
    utilsService.setTitleByRoute();
    utilsService.deleteOldLocalStorageInfo();

    // Initialize FCM after a short delay to ensure Firebase is ready
    setTimeout(() => this.initializeFCM(), 1000);
  }

  private async initializeFCM() {
    try {
      const supported = await isSupported();
      if (!supported) {
        console.warn('This browser does not support Firebase Cloud Messaging');
        return;
      }

      // Get or initialize the FCM app
      let fcmApp;
      try {
        fcmApp = getApp('fcm-app');
      } catch (e) {
        console.log('FCM app not found, initializing...');
        fcmApp = initializeApp(environment.fcmConfig, 'fcm-app');
      }

      if (fcmApp) {
        try {
          const messaging = getMessaging(fcmApp);
          // Request notification permission if not already granted
          this.requestNotificationPermission(messaging);
        } catch (messagingError) {
          console.error('Error initializing FCM messaging:', messagingError);
        }
      }
    } catch (e) {
      console.error('Error in FCM initialization:', e);
    }
  }

  private async requestNotificationPermission(messaging: any) {
    try {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        console.log('Notification permission granted.');
        // You can get the FCM token here if needed
        // const token = await getToken(messaging);
      } else {
        console.log('Unable to get permission to notify.');
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
    }
  }
}
